import { cleanHTML } from "@/utils/content";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { AddEditNews } from "./startup/add-edit-news";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { deleteVcStartupNews } from "@/services/api/Startup";
import toast from "react-hot-toast";
import { Pencil, TrashIcon } from "lucide-react";

export default function StartupModalNews({
  selectedStartup,
  setSelectedStartup,
  isOpen,
  setIsOpen,
  vc_startup_news,
  vc_startup_news_by_funding,
  selectedFunding,
  setSelectedFunding,
  noEdit,
  user,
}) {
  function closeModal() {
    if (typeof isOpen == "boolean") {
      setIsOpen(false);
    } else {
      setSelectedFunding(null);
    }
  }

  const handleModalOpenChange = (open) => {
    if (!open) {
      closeModal();
    }
  };

  function openModal() {
    setIsOpen(true);
  }

  const [arr, setArr] = useState([]);
  const [VC, setVCs] = useState([]);

  useEffect(() => {
    if (vc_startup_news) {
      setArr(vc_startup_news[selectedStartup.id]);
    }
    if (vc_startup_news_by_funding) {
      setArr(
        vc_startup_news_by_funding.filter(
          (n) => n.funding_round == selectedFunding?.id
        )
      );
    }
  }, [vc_startup_news_by_funding, vc_startup_news]);

  const handleDeleteNews = async (newsId) => {
    // Call the onDeleteNews prop function
    try {
      await deleteVcStartupNews(newsId);
      // Update the local state to remove the deleted news
      setArr(prev => prev.filter(news => news.id !== newsId));
      toast.success("Successfully delete startup news!");
    } catch (error) {
      toast.error("Failed to delete startup news!");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleModalOpenChange} className='my-0'>
      <DialogContent
        className='max-w-3xl p-0 bg-white text-background'
        onClick={(e) => e.stopPropagation()}
      >
        <ScrollArea className='max-h-[100vh] lg:max-h-[90vh]'>
          <div
            className='-my-3 lg:-my-2.5 divide-y divide-gray-200 p-4 lg:p-6'
            onClick={(e) => e.stopPropagation()}
          >
            {arr.map((news, index) => (
              <div
                key={index}
                className='relative py-3 lg:py-2.5'
                onClick={(e) => e.stopPropagation()}
              >
                <div className='flex items-start justify-between gap-4'>
                  <h2 className='flex-1 mb-1 text-base font-semibold leading-tight lg:text-lg'>
                    {news.title}
                  </h2>
                  {(!noEdit &&
                    selectedStartup.owner_id &&
                    selectedStartup.owner_id == user?.id) ||
                    (selectedStartup.accesses &&
                      user?.id &&
                      selectedStartup.accesses.find(
                        (acc) => acc.user_id == user?.id
                      )) ||
                    (user?.roles === "admin-team" && (
                      <div className='flex items-center flex-shrink-0 gap-1.5'>
                        <AddEditNews
                          startup={selectedStartup}
                          fundingRound={selectedFunding}
                          isTable={false}
                          editedNews={news}
                          closeParent={closeModal}
                        />
                        <button
                          type='button'
                          className='inline-flex items-center justify-center text-red-600 transition-colors border border-red-200 rounded-full hover:bg-red-50 w-9 h-9'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNews(news.id);
                          }}
                        >
                          <TrashIcon className='w-3.5 h-3.5' />
                        </button>
                      </div>
                    ))}
                </div>
                <p className='mb-2.5 text-xs'>
                  {news.publisher_name ? `by ${news.publisher_name}` : ""}
                  {news.publisher_name && news.published_at ? " on " : ""}
                  {news.published_at
                    ? dayjs(news.published_at).format("MMMM D, YYYY")
                    : ""}
                </p>
                <p className='text-xs leading-normal opacity-60 line-clamp-2'>
                  {news.content ? cleanHTML(news.content) : ""}
                </p>

                {selectedFunding && (
                  <div className='mt-3'>
                    <p className='text-sm font-medium'>Mentioned VC</p>
                    <div className='flex flex-wrap gap-1'>
                      {selectedFunding.investors
                        .filter((inv) => {
                          try {
                            // Handle both cases where vc_ids is already an array or needs to be parsed
                            const vcIds = Array.isArray(news.vc_ids)
                              ? news.vc_ids
                              : news.vc_ids
                                ? JSON.parse(news.vc_ids)
                                : [];
                            return Array.isArray(vcIds) && vcIds.includes(inv.id);
                          } catch (error) {
                            console.warn('Error parsing vc_ids:', error);
                            return false;
                          }
                        })
                        .map((data) => (
                          <Badge
                            className='mb-1 mr-1'
                            variant='secondary'
                            key={data.id}
                          >
                            {data.name}
                          </Badge>
                        ))}
                    </div>
                  </div>
                )}

                <div className='mt-4 text-sm'>
                  <a
                    rel='noopener noreferrer'
                    target='_blank'
                    className='inline-flex px-4 py-2 text-xs font-medium text-white rounded-md bg-background'
                    href={news.publisher_link}
                  >
                    Visit Link
                  </a>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
